import type { IsoTimestamp, Uuid } from "../types/base.js";
import type { Item, Trip, TripItem } from "../types/entities.js";

export interface FinishTripInput {
  items: readonly Item[];
  tripId: Uuid;
  familyId: Uuid;
  storeId: Uuid | null;
  startedAt: IsoTimestamp;
  now: IsoTimestamp;
  actorId: Uuid | null;
  /**
   * Wer den Einkauf begonnen hat. Fehlt der Wert, war es die handelnde Person.
   * Getrennt von `actorId`, seit der Einkauf beim Betreten des Einkaufsmodus
   * entsteht: Abschließen darf auch jemand anderes, und dann soll nicht
   * plötzlich sie oder er den Einkauf begonnen haben.
   */
  startedBy?: Uuid | null;
  totalCents?: number | null;
  /** Erzeugt die IDs der Einkaufspositionen. Getrennt, damit Tests stabil sind. */
  newId: () => Uuid;
}

/**
 * Was beim Abschließen eines Einkaufs passiert, als reine Berechnung.
 * Der Aufrufer schreibt das Ergebnis, damit diese Logik ohne Datenbank
 * geprüft werden kann.
 */
export interface FinishTripPlan {
  trip: Trip;
  tripItems: TripItem[];
  /** Geänderte Artikel: archiviert (Grabstein) oder wieder offen. */
  itemUpdates: Item[];
}

/**
 * Regel aus dem Konzept: erledigte Artikel wandern in den Einkauf und
 * verschwinden aus der Liste. Wiederkehrende Artikel bleiben, sie werden nur
 * wieder offen gesetzt. Offene Artikel bleiben unangetastet, der Einkauf war
 * nicht vollständig und das ist kein Fehler.
 */
export function planFinishTrip(input: FinishTripInput): FinishTripPlan {
  const { items, tripId, familyId, storeId, startedAt, now, actorId, newId } = input;
  const checked = items.filter((item) => item.is_checked && item.deleted_at === null);

  const trip: Trip = {
    id: tripId,
    family_id: familyId,
    store_id: storeId,
    started_at: startedAt,
    finished_at: now,
    total_cents: input.totalCents ?? null,
    started_by: input.startedBy === undefined ? actorId : input.startedBy,
    updated_at: now,
    client_updated_at: now,
    deleted_at: null,
  };

  const tripItems: TripItem[] = checked.map((item) => ({
    id: newId(),
    family_id: familyId,
    trip_id: tripId,
    product_id: item.product_id,
    name: item.name,
    quantity: item.quantity,
    unit: item.unit,
    category: item.category,
    price_cents: null,
    updated_at: now,
    client_updated_at: now,
    deleted_at: null,
  }));

  const itemUpdates: Item[] = checked.map((item) =>
    item.is_recurring
      ? { ...item, is_checked: false, checked_at: null, checked_by: null, unavailable_at: null, updated_at: now, client_updated_at: now }
      : { ...item, deleted_at: now, updated_at: now, client_updated_at: now },
  );

  // "Nicht da" gilt nur für diesen Einkauf. Beim nächsten Mal ist der Artikel
  // wieder ein offener Artikel wie jeder andere.
  for (const item of items) {
    if (item.deleted_at !== null || item.is_checked || item.unavailable_at === null) continue;
    itemUpdates.push({ ...item, unavailable_at: null, updated_at: now, client_updated_at: now });
  }

  return { trip, tripItems, itemUpdates };
}
